import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import AkseldriftChart from './AkseldriftChart'
import { DRIVES, type AkseldriftOversikt as Oversikt, type AkseldriftTidsserie, type Drive, type Periods } from './types'

const int = (n: number) => n.toLocaleString('nb-NO')
const pct = (n: number | null) =>
  n === null ? '–' : `${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
const signed = (n: number) =>
  `${n > 0 ? '+' : ''}${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
const changeColor = (n: number) =>
  n > 0 ? 'text-green-600 dark:text-green-400' : n < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'

/** Teknisk › Akseldrift › Oversikt. `queryKey` is the applied query as a JSON string. */
export default function AkseldriftOversikt({ queryKey, onSeeAll, onPeriods }: {
  queryKey: string
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
  onSeeAll: (tab: 'merke' | 'modell', drive: Drive) => void
}) {
  const { t } = useTranslation('teknisk')
  const [oversikt, setOversikt] = useState<Oversikt | null>(null)
  const [series, setSeries] = useState<AkseldriftTidsserie | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [topView, setTopView] = useState<'models' | 'brands'>('models')

  useLatest(alive => {
    const params = JSON.parse(queryKey)
    setLoading(true)
    setError(null)
    Promise.all([publicAPI.getAkseldriftOversikt(params), publicAPI.getAkseldriftTidsserie(params)])
      .then(([o, s]) => {
        if (!alive()) return
        setOversikt(o.data)
        setSeries(s.data)
        onPeriods(o.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('akseldrift.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!oversikt || !series) return null

  const byDrive = Object.fromEntries(oversikt.akseldrift.map(a => [a.akseldrift, a])) as Record<Drive, Oversikt['akseldrift'][number]>
  const driveLabel = (d: Drive) => t(`akseldrift.drive.${d}` as const)
  const noComparison = (
    <span className="text-gray-400" title={t('akseldrift.noComparisonTitle')}>{t('akseldrift.vsLastYear')}: –</span>
  )

  if (oversikt.total.count === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('akseldrift.noData')}</p>
  }

  const diff = (a: number | null, b: number | null) => (a === null || b === null ? null : a - b)
  const leasingDiff = diff(byDrive['4wd'].leasing_share, byDrive['2wd'].leasing_share)
  const loanDiff = diff(byDrive['4wd'].loan_share, byDrive['2wd'].loan_share)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="akseldrift-kpi">
        {DRIVES.map(d => {
          const row = byDrive[d]
          return (
            <div key={d} className="card p-4" data-testid={`akseldrift-kpi-${d}`}>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{driveLabel(d)}</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1" data-testid={`akseldrift-share-${d}`}>
                {pct(row.share)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('akseldrift.countCars', { count: int(row.count) })}
              </p>
              <p className="text-sm mt-1" data-testid={`akseldrift-change-${d}`}>
                {row.share_change_pp === null
                  ? noComparison
                  : <span className={changeColor(row.share_change_pp)}>{t('akseldrift.ppVsLastYear', { value: signed(row.share_change_pp) })}</span>}
              </p>
            </div>
          )
        })}
        <div className="card p-4" data-testid="akseldrift-kpi-total">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('akseldrift.total')}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">{int(oversikt.total.count)}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('akseldrift.totalSubtitle')}</p>
          <p className="text-sm mt-1" data-testid="akseldrift-change-total">
            {oversikt.total.change_pct === null
              ? noComparison
              : <span className={changeColor(oversikt.total.change_pct)}>{t('akseldrift.pctVsLastYear', { value: signed(oversikt.total.change_pct) })}</span>}
          </p>
        </div>
      </div>

      <AkseldriftChart series={series} />

      <div className="card p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('akseldrift.leasingLoanTitle')}</h3>
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact" data-testid="akseldrift-leasing-loan">
            <thead>
              <tr>
                <th></th>
                {DRIVES.map(d => <th key={d} className="text-right">{driveLabel(d)}</th>)}
                <th className="text-right">{t('akseldrift.difference')}</th>
              </tr>
            </thead>
            <tbody>
              {([
                ['leasing', t('akseldrift.leasingShare'), leasingDiff],
                ['loan', t('akseldrift.loanShare'), loanDiff],
              ] as const).map(([kind, label, delta]) => (
                <tr key={kind}>
                  <td>{label}</td>
                  {DRIVES.map(d => (
                    <td key={d} className="text-right">
                      {pct(kind === 'leasing' ? byDrive[d].leasing_share : byDrive[d].loan_share)}
                    </td>
                  ))}
                  <td className={`text-right ${delta === null ? '' : changeColor(delta)}`}>
                    {delta === null ? '–' : signed(delta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('akseldrift.topTitle')}</h3>
          <div className="flex gap-2" role="group">
            {(['models', 'brands'] as const).map(view => (
              <button
                key={view}
                onClick={() => setTopView(view)}
                aria-pressed={topView === view}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  topView === view
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
              >
                {view === 'models' ? t('akseldrift.topModels') : t('akseldrift.topBrands')}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {DRIVES.map(d => (
            <div key={d} className="card p-4" data-testid={`akseldrift-top-${d}`}>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{driveLabel(d)}</h4>
              <div className="overflow-x-auto">
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="w-8">#</th>
                      <th>{topView === 'models' ? t('akseldrift.colModel') : t('akseldrift.colBrand')}</th>
                      <th className="text-right">{t('akseldrift.colCount')}</th>
                      <th className="text-right">{t('akseldrift.colShare', { drive: driveLabel(d) })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topView === 'models'
                      ? oversikt.top_models[d].map((m, i) => (
                        <tr key={m.master_model_id}>
                          <td>{i + 1}</td>
                          <td><span className="text-gray-500 dark:text-gray-400">{m.brand}</span> {m.model}</td>
                          <td className="text-right">{int(m.count)}</td>
                          <td className="text-right">{pct(m.share)}</td>
                        </tr>
                      ))
                      : oversikt.top_brands[d].map((b, i) => (
                        <tr key={`${b.brand}-${i}`}>
                          <td>{i + 1}</td>
                          <td>{b.brand}</td>
                          <td className="text-right">{int(b.count)}</td>
                          <td className="text-right">{pct(b.share)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={() => onSeeAll(topView === 'models' ? 'modell' : 'merke', d)}
                className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('akseldrift.seeAll')} →
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
